if (!customElements.get("variant-picker")) {
   class VariantPicker extends HTMLElement {
      constructor() {
         super();
         if (Shopify.designMode) {
            setTimeout(() => this.init(), 200);
         } else {
            this.init();
         }
      }

      init() {
         this.selectors = {
            error: ".m-product-form-message",
            variantIdInput: '[name="id"]',
            pickerFields: ["[data-picker-field]"],
            optionNodes: [".m-product-option--node"],
            productCustomLabel: "[data-custom-label]",
            productSubtitle: "[data-product-subtitle]",
            productBanner: "[data-variant-banner]",
            productSku: "[data-variant-sku]",
            productBarcode: "[data-variant-barcode]",
            productAvailability: "[data-availability]",
            productInventoryStatus: "[data-variant-inventory-status]",
            shareUrlInput: "[data-share-url]",
            stockCountdown: ".prod__stock-countdown",
            productWrapper: ".m-main-product--wrapper",
         };

         this.productWrapper = this.closest(this.selectors.productWrapper);
         this.productForm = this.productWrapper.querySelector("product-form");
         this.domNodes = queryDomNodes(this.selectors, this.productWrapper);
         this.ctaBtnText = window.MinimogStrings.addToCart;

         const themeProducts = window._themeProducts || {};
         const {
            productId,
            sectionId,
            productUrl,
            hasOnlyDefaultVariant,
            showFeaturedMedia,
         } = this.dataset;

         Object.assign(this, {
            productId,
            sectionId,
            productUrl,
            hasOnlyDefaultVariant: hasOnlyDefaultVariant === "true",
            showFeaturedMedia: showFeaturedMedia === "true",
            variantData: this.getVariantData(),
            variantMetafieldData: this.getVariantMetafieldData(),
            productData: Object.assign(
               this.getProductJson(),
               themeProducts[productId]
            ),
         });

         this.handleQueryString();
         this.handleVariantGroupImages();
         this.selectInitialVariant();

         this.setupEventListeners();
         this.initOptionSwatches();
      }

      handleQueryString() {
         const hasQueryString = new URLSearchParams(window.location.search).has("variant");
         const disableSelectedVariantDefault =
            this.dataset.disableSelectedVariantDefault === "true";
         const hasMultipleVariants =
            this.productData &&
            this.productData.variants &&
            this.productData.variants.length > 1;

         this.selectedVariantDefault =
            disableSelectedVariantDefault &&
            hasMultipleVariants &&
            !hasQueryString;

         if (this.selectedVariantDefault) {
            this.disableSelectedVariantDefault();
         }
      }

      handleVariantGroupImages() {
         if (this.dataset.enableVariantGroupImages === "true") {
            this.enableVariantGroupImages = true;
            this.variantGroupImages = this.getVariantGroupImageData();
         } else {
            this.enableVariantGroupImages = false;
         }
      }

      selectInitialVariant() {
         const variantIdInput = this.productWrapper.querySelector(
            this.selectors.variantIdInput
         );
         const selectedVariantId = Number(variantIdInput.value);
         const currentVariant = this.productData.variants.find(
            (variant) => variant.id === selectedVariantId
         );
         let updateButtonText = "";
         if (currentVariant && currentVariant.inventory_quantity <= 0) {
            updateButtonText = "Sold Out";
         } else {
            updateButtonText = window.MinimogStrings.addToCart;
         }

         const variantMetafield = this.variantMetafieldData.find(
            (metafield) => metafield.id === `${currentVariant.id}`
         );
         const status = String(variantMetafield?.metafields?.inventory_status ?? "");
         if (status.toLowerCase().includes("preorder")) {
            updateButtonText = "Preorder"
         }
         if (status.toLowerCase().includes("coming soon")) {
            updateButtonText = "Coming soon"
         }
         if (status.toLowerCase().includes("backorder")) {
            updateButtonText = "Backorder"
         }

         this.selectedVariant = variantIdInput;
         this.currentVariant = currentVariant;
         this.productData.current_variant_id = currentVariant
            ? currentVariant.id
            : null;
         this.productData.initialVariant = currentVariant;

         if (currentVariant) {
            const isDiabled = updateButtonText === "Coming soon" ? true : !this.currentVariant.available;
            this.getDataImageVariant(currentVariant.id);
            this.hideSoldOutAndUnavailableOptions();
            this.updateButton(isDiabled, updateButtonText);
         }
      }

      setupEventListeners() {
         this.hasSetupMediaData = false;
         const mediaGalleryHandler = this.getMediaGallery.bind(this);

         mediaGalleryHandler();
         ["matchMobile", "unmatchMobile"].forEach((event) =>
            document.addEventListener(event, mediaGalleryHandler)
         );

         this.addEventListener("change", this.onVariantChange);
      }

      getMediaGallery() {
         return this.waitForMediaGallery().then(
            this.setupMediaGallery.bind(this)
         );
      }

      waitForMediaGallery() {
         return new Promise((resolve) => {
            const checkMediaGallery = () => {
               this.mediaGallery =
                  this.productWrapper.querySelector("media-gallery");
               this.mediaGalleryMobile = this.productWrapper.querySelector(
                  "media-gallery-mobile"
               );

               const isMediaGalleryReady =
                  this.mediaGallery &&
                  (this.mediaGallery.view === "featured-product" ||
                     this.mediaGallery.view === "quick-view") &&
                  this.mediaGallery.mediaMode;

               const isBothGalleriesReady =
                  this.mediaGallery &&
                  this.mediaGalleryMobile &&
                  this.mediaGallery.mediaMode &&
                  this.mediaGalleryMobile.mediaMode;

               if (isMediaGalleryReady || isBothGalleriesReady) {
                  resolve();
               } else {
                  setTimeout(checkMediaGallery, 100);
               }
            };

            checkMediaGallery();
         });
      }

      setupMediaGallery() {
         const isMobile = MinimogTheme.config.mqlMobile;
         const effectiveMediaGallery = isMobile
            ? this.mediaGalleryMobile || this.mediaGallery
            : this.mediaGallery;
         if (!effectiveMediaGallery) return;
         if (this.media !== effectiveMediaGallery) {
            this.hasSetupMediaData = false;
            this.slides = undefined;
            this.slidesNav = undefined;
            this.mediaItems = undefined;
         }
         this.media = effectiveMediaGallery;

         if (!this.hasSetupMediaData) {
            if (isMobile || effectiveMediaGallery.mediaMode === "slider") {
               this.slides =
                  effectiveMediaGallery.slider &&
                  effectiveMediaGallery.slider.slides;
               this.slidesNav =
                  effectiveMediaGallery.navSlider &&
                  effectiveMediaGallery.navSlider.slides;
            } else {
               this.mediaItems = effectiveMediaGallery.querySelectorAll(
                  ".m-product-media--item"
               );
            }

            this.layout = !isMobile ? effectiveMediaGallery.layout : undefined;
         }

         if (
            !this.showFeaturedMedia ||
            (this.enableVariantGroupImages && this.variantGroupImages.enable)
         ) {
            this.updateMedia();
         }
         this.hasSetupMediaData = true;
      }

      disableSelectedVariantDefault() {
         // Reset radio options
         this.querySelectorAll('input[type="radio"]').forEach((radioOption) => {
            radioOption.checked = false;
         });

         // Clear data-selected-value for radio picker fields
         this.querySelectorAll("[data-picker-field='radio']").forEach(
            (radio) => {
               radio.setAttribute("data-selected-value", "");
            }
         );

         // Reset select elements and clear data-selected-value for select picker fields
         this.querySelectorAll("[data-picker-field='select']").forEach(
            (pickerSelect) => {
               const selectElm = pickerSelect.querySelector("select");
               if (selectElm) selectElm.value = "";
               pickerSelect.setAttribute("data-selected-value", "");
            }
         );

         // Clear text content for selected option labels
         this.querySelectorAll(".option-label--selected").forEach((label) => {
            label.textContent = "";
         });

         // Reset variant ID if applicable
         if (this.productForm) {
            const variantIdEl = this.productForm.querySelector("[name='id']");
            if (variantIdEl && !this.hasOnlyDefaultVariant) {
               variantIdEl.value = "";
            }
         }
      }

      setProductAsUnavailable() {
         this.updateButton(true); // Disable button, clear text, mark as unavailable
         this.setUnavailable();
      }

      updateProductDetailsForVariant() {
         // Fetch additional data and update UI components to reflect the selected variant
         this.getDataImageVariant(this.currentVariant.id);
         this.updateMedia();
         this.updateBrowserHistory();
         this.updateVariantInput();
         this.updateProductMeta();
         this.updatePrice();

         // Update the button based on availability of the current variant
         const isDiabled = this.ctaBtnText === "Coming soon" ? true : !this.currentVariant.available;
         // const buttonText = isSoldOut ? window.MinimogStrings.soldOut : "";
         this.updateButton(isDiabled, this.ctaBtnText);

         // Hide options that are sold out or unavailable
         this.hideSoldOutAndUnavailableOptions();
      }

      onVariantChange(event) {
         // Give the control that actually changed priority over hidden duplicates.
         this.getSelectedOptions(event);
         this.getSelectedVariant();

         // Always perform these updates on variant change
         this.updatePickupAvailability();
         this.removeErrorMessage();

         // If there's no current variant, set the product as unavailable and return early
         if (!this.currentVariant) {
            this.setProductAsUnavailable();
            return;
         }

         // Update the product details for the selected variant
         this.updateProductDetailsForVariant();
         this.updateVariantPickerVisibility();
         this.sqUpdateVariantPickerOptions();

         // Emit a custom event signaling a variant change
         const variantChangeEvent = new CustomEvent("variant:changed", {
            detail: { variant: this.currentVariant },
         });
         window.MinimogEvents.emit(
            `${this.productId}__VARIANT_CHANGE`,
            this.currentVariant,
            this
         );
         document.dispatchEvent(variantChangeEvent);
      }

      getDataImageVariant(variantId) {
         if (!this.variantGroupImages || !this.variantGroupImages.enable)
            return;

         const currentVariantMedia = this.variantGroupImages.mapping.find(
            (variant) => Number(variant.id) === variantId
         );
         if (currentVariantMedia) {
            this.currentVariantMedia = currentVariantMedia.media;
         }
      }

      getProductJson() {
         try {
            const productDataElement = this.productWrapper.querySelector(
               "#productData[type='application/json']"
            );
            if (!productDataElement)
               throw new Error("Fallback product data element not found.");
            return JSON.parse(productDataElement.textContent);
         } catch (error) {
            console.error("Error loading fallback product data:", error);
            return null; // Return null as a default value
         }
      }
      sqGetVariantFromOptionArray(product, options, metafieldData) {
         _validateProductStructure(product);
         _validateOptionsArray(options);
         var result = product.variants.filter(function (variant) {
            const variantMetafield = metafieldData.find(
               (metafield) => metafield.id === `${variant.id}`
            );
            if (variantMetafield?.metafields?.variant_visibility === false) {
               return false;
            }
            return options.every(function (option, index) {
               return variant.options[index] === option;
            });
         });
         return result[0] || null;
      }

      getSelectedVariant() {
         let variant = null;
         const options = [...this.options];
         for (let length = options.length; length > 0; length--) {
            variant = this.sqGetVariantFromOptionArray(
               this.productData, options.slice(0, length), this.variantMetafieldData
            );
            if (variant) break;
         }
         this.currentVariant = variant;
         if (variant) {
            this.options = [...variant.options];
            this.productData.current_variant_id = variant.id;
            this.updateSelectedOptions();
         }
      }

      getSelectedOptions(event) {
         const normalize = value => String(value ?? "").trim().toLowerCase();
         const fields = Array.from(this.querySelectorAll("[data-picker-field]"));
         const changedField = event?.target?.closest?.("[data-picker-field]");
         const readValue = field => {
            const select = field.querySelector("select");
            if (select) return select.value || undefined;
            return field.querySelector('input[type="radio"]:checked')?.value;
         };
         // One value per product option, regardless of duplicate desktop/mobile controls.
         this.options = this.productData.options.map((option, index) => {
            const name = normalize(option.name ?? option);
            const matches = fields.filter(field => normalize(field.dataset.optionName) === name);
            if (changedField && matches.includes(changedField)) {
               const value = readValue(changedField);
               if (value !== undefined) return value;
            }
            const visible = matches.filter(field => field.getClientRects().length > 0);
            for (const field of [...visible, ...matches]) {
               const value = readValue(field);
               if (value !== undefined) return value;
            }
            // Keep omitted options such as Status in their original position.
            return this.currentVariant?.options?.[index];
         });
      }

      sqUpdateVariantPickerOptions() {
         const normalize = value => String(value ?? "").trim().toLowerCase();
         const names = this.productData.options.map(option => normalize(option.name ?? option));
         const deviceIndex = names.indexOf("device");
         const colorIndex = names.findIndex(name => name === "color" || name === "colour");
         if (colorIndex < 0) return;
         const selectedDevice = deviceIndex >= 0 ? this.options?.[deviceIndex] : undefined;
         const candidates = this.productData.variants.filter(variant =>
            deviceIndex < 0 || selectedDevice === undefined || variant.options[deviceIndex] === selectedDevice
         );
         this.querySelectorAll("[data-picker-field]").forEach(field => {
            if (!["color", "colour"].includes(normalize(field.dataset.optionName))) return;
            field.querySelectorAll(".m-product-option--node").forEach(node => {
               const label = node.querySelector("label[data-option-type='image']");
               if (!label) return;
               const variant = candidates.find(item =>
                  item.options[colorIndex] === node.dataset.value && item.featured_image?.src
               );
               const src = variant?.featured_image?.src;
               if (!src) return;
               const decoded = src.replaceAll(/&quot;/g, "'").replaceAll(/&amp;/g, "&");
               // Preserve existing CDN parameters while setting the swatch size.
               const url = new URL(decoded, window.location.origin);
               url.searchParams.set("width", "110");
               url.searchParams.set("height", "150");
               url.searchParams.set("crop", "center");
               label.style.backgroundImage = `url(${url.toString()})`;
               label.style.backgroundSize = "cover";
               label.style.backgroundPosition = "center center";
            });
         });
      }

      updateSelectedOptions() {
         const normalize = value => String(value ?? "").trim().toLowerCase();
         const names = this.productData.options.map(option => normalize(option.name ?? option));
         // Update every representation by option name, not by DOM index.
         this.querySelectorAll("[data-picker-field]").forEach(field => {
            const index = names.indexOf(normalize(field.dataset.optionName));
            if (index < 0) return;
            const value = this.options[index];
            if (value === undefined) return;
            const select = field.querySelector("select");
            if (select) select.value = value;
            field.querySelectorAll('input[type="radio"]').forEach(input => {
               input.checked = input.value === value;
            });
            field.value = value;
            field.dataset.selectedValue = value;
            const label = field.querySelector(".option-label--selected");
            if (label) {
               label.textContent = value;
               label.classList.remove("m:text-color-error");
            }
         });
      }

      removeLoading(mediaElement) {
         if (!mediaElement) return;

         mediaElement.removeAttribute("data-media-loading");
         const firstChild = mediaElement.firstElementChild;
         if (firstChild) firstChild.style.opacity = 1;
      }

      handleImageZoom(mediaItems, mediaContext) {
         const customData = mediaItems.map((item) => {
            const { mediaType, index } = item.dataset;
            if (mediaType === "image") {
               const { mediaSrc, mediaWidth, mediaHeight, mediaAlt } =
                  item.querySelector(".m-product-media").dataset;
               return {
                  src: mediaSrc,
                  width: parseInt(mediaWidth),
                  height: parseInt(mediaHeight),
                  alt: mediaAlt,
                  id: index,
               };
            }
            return {
               html: `<div class="pswp__${mediaType}">${item.innerHTML}</div>`,
               type: mediaType,
               id: index,
            };
         });

         if (mediaContext && mediaContext.lightbox) {
            mediaContext.lightbox.destroy();
            mediaContext.handlePhotoswipe(customData);
            mediaContext.initPhotoswipe();
         }
      }

      updateMedia() {
         if (!this.currentVariant || !this.media) return;

         // Define main and navigation items arrays
         let mainItems = [],
            navItems = [];

         const reorderItems = (
            referenceItems,
            items,
            isNav = false,
            isSlider = false
         ) => {
            let reorderedItems = [];

            referenceItems.forEach((referenceItem, index) => {
               const foundItem = items.find(
                  (item) =>
                     item.querySelector("[data-media-id]").dataset.mediaId ===
                     referenceItem
               );
               if (foundItem) {
                  foundItem.dataset.index = index;
                  foundItem.dataset.swiperSlideIndex = index;
                  reorderedItems.push(foundItem);
               }
            });

            items.forEach((item) => {
               const dataIdMedia =
                  item.querySelector("[data-media-id]").dataset.mediaId;
               if (
                  !referenceItems.includes(dataIdMedia) &&
                  !reorderedItems.includes(item)
               ) {
                  item.dataset.index = reorderedItems.length;
                  item.dataset.swiperSlideIndex = reorderedItems.length;
                  reorderedItems.push(item);
               }
            });

            if (isSlider) {
               reorderedItems.forEach((item) => {
                  if (isNav) {
                     item.classList.toggle(
                        "swiper-slide-thumb-active",
                        item.dataset.swiperSlideIndex === "0"
                     );
                  } else {
                     item.classList.toggle(
                        "swiper-slide-active",
                        item.dataset.swiperSlideIndex === "0"
                     );
                  }
               });
            }

            return reorderedItems;
         };

         // Common function to process slides
         const processItems = (currentItems, items) => {
            currentItems.forEach((slide) => {
               const dataIdMedia =
                  slide.querySelector("[data-media-id]").dataset.mediaId;

               const isCurrentVariantMedia =
                  this.currentVariantMedia &&
                  this.currentVariantMedia.length > 0 &&
                  this.currentVariantMedia.includes(dataIdMedia);
               const isNotImageType =
                  !slide.classList.contains("media-type-image");

               if (
                  isCurrentVariantMedia ||
                  isNotImageType ||
                  !this.currentVariantMedia ||
                  this.currentVariantMedia.length === 0
               ) {
                  items.push(slide);
               }
            });
         };

         // Process based on media mode
         if (this.variantGroupImages && this.variantGroupImages.enable) {
            if (this.media.mediaMode === "slider" && this.slides) {
               // Re-render main media
               processItems(this.slides, mainItems);
               const reorderedMainItems = reorderItems(
                  this.currentVariantMedia,
                  mainItems,
                  false,
                  true
               );
               // Update slider with new slides
               this.media.slider.removeAllSlides();
               this.media.slider.appendSlide(reorderedMainItems);
               this.media.slider.slideToLoop(
                  this.layout === "layout-7" ? 1 : 0
               );
               this.media.handleSlideChange();

               // Re-render nav media if available
               if (this.slidesNav) {
                  processItems(this.slidesNav, navItems);
                  const reorderedNavItems = reorderItems(
                     this.currentVariantMedia,
                     navItems,
                     true,
                     true
                  );
                  if (this.media.navSlider) {
                     this.media.navSlider.removeAllSlides();
                     this.media.navSlider.appendSlide(reorderedNavItems);
                     this.media.navSlider.slideToLoop(0);
                  }
               }
            } else {
               // Ensure mediaWrapper is available before proceeding.
               const mediaWrapper = this.media.querySelector(
                  ".m-product-media--list"
               );
               if (!mediaWrapper) return;

               // Use a more functional approach to filter and process items.
               processItems(this.mediaItems, mainItems);

               const reorderedItems = reorderItems(
                  this.currentVariantMedia,
                  mainItems
               );

               // Clear the mediaWrapper content once before appending new items.
               mediaWrapper.innerHTML = "";

               // Process and append the main items to the mediaWrapper.
               reorderedItems.forEach((item, index) => {
                  // Additional layout-specific adjustments can be made here.
                  if (this.layout === "layout-2") {
                     item.classList.remove("m-col-span-2");
                     if (index % 3 === 0) {
                        item.classList.add("m-col-span-2");
                     }
                  }
                  mediaWrapper.append(item); // Append each item to the mediaWrapper.
               });
            }
         } else {
            // Initialize mediaSize to 0 by default
            let mediaSize = 0;

            // Check if this.media and this.media.dataset.mediaSize exist before parsing
            if (this.media && "mediaSize" in this.media.dataset) {
               mediaSize = parseInt(this.media.dataset.mediaSize, 10);
            }

            // Proceed only if mediaSize is a positive number
            if (mediaSize > 0) {
               this.media.setActiveMedia(this.currentVariant);
            }
         }
         // Common operations after media update
         this.removeLoading(this.media);
         this.handleImageZoom(mainItems, this.media);
      }

      updateBrowserHistory() {
         if (this.currentVariant && this.dataset.updateUrl !== "false") {
            const url = new URL(window.location.href);
            url.searchParams.set("variant", this.currentVariant.id);
            window.history.replaceState({}, "", url.toString());
         }
      }

      updateVariantInput() {
         document
            .querySelectorAll(
               `#product-form-${this.sectionId}, #product-form-installment`
            )
            .forEach((productForm) => {
               const variantIdInput = productForm.querySelector(
                  this.selectors.variantIdInput
               );
               if (variantIdInput) {
                  variantIdInput.value = this.currentVariant.id;
                  variantIdInput.dispatchEvent(
                     new Event("input", { bubbles: true })
                  );
               }
            });
      }

      updatePickupAvailability() {
         const pickUpAvailability = this.productWrapper.querySelector(
            "pickup-availability"
         );

         if (!pickUpAvailability) return;

         if (!this.currentVariant || !this.currentVariant.available) {
            pickUpAvailability.removeAttribute("available");
            pickUpAvailability.innerHTML = "";
            return;
         }

         pickUpAvailability.fetchAvailability(this.currentVariant.id);
      }

      removeErrorMessage() {
         const section = this.closest("section");
         if (!section) return;

         const productForm = section.querySelector("product-form");
         if (productForm) {
            productForm.handleErrorMessage();
         }
      }

      updatePrice() {
         const classes = {
            onSale: "m-price--on-sale",
            soldOut: "m-price--sold-out",
         };

         const selectors = {
            priceWrapper: ".m-price",
            salePrice: ".m-price-item--sale",
            compareAtPrice: [".m-price-item--regular"],
            unitPrice: ".m-price__unit",
            unitPriceWrapper: ".m-price__unit-wrapper",
            saleBadge: ".m-price__badge-sale",
            saleAmount: "[data-saved-price]",
         };

         const mainProductPrices = this.productWrapper.querySelector(
            ".main-product__block-price"
         );
         if (!mainProductPrices) return;

         const moneyFormat = window.MinimogSettings.money_format;
         const {
            priceWrapper,
            salePrice,
            unitPrice,
            compareAtPrice,
            saleBadge,
            saleAmount,
            unitPriceWrapper,
         } = queryDomNodes(selectors, mainProductPrices);

         const { compare_at_price, price, unit_price_measurement, available } =
            this.currentVariant;
         const saleBadgeType = priceWrapper.dataset.saleBadgeType;

         const onSale = compare_at_price && compare_at_price > price;
         const soldOut = !available;

         priceWrapper.classList.toggle(classes.onSale, onSale);
         priceWrapper.classList.toggle(classes.soldOut, soldOut);
         priceWrapper.classList.remove("m:visibility-hidden");

         if (salePrice) {
            salePrice.innerHTML = formatMoney(price, moneyFormat);
         }

         if (compareAtPrice) {
            compareAtPrice.forEach(function (item) {
               const displayPrice = onSale ? compare_at_price : price;
               item.innerHTML = formatMoney(displayPrice, moneyFormat);
            });
         }

         if (saleBadge && onSale && saleBadgeType !== "text") {
            let value;
            if (saleBadgeType === "fixed_amount") {
               value = formatMoney(compare_at_price - price, moneyFormat);
            } else {
               const saving =
                  Math.round(
                     ((compare_at_price - price) * 100) / compare_at_price
                  ) + "%";
               value = saving;
            }
            if (saleAmount) {
               saleAmount.innerHTML = value;
            }
         }

         if (unit_price_measurement && unitPrice) {
            unitPriceWrapper.classList.remove("m:hidden");
            const unitPriceContent =
               "<span>" +
               formatMoney(this.currentVariant.unit_price, moneyFormat) +
               "</span>/<span data-unit-price-base-unit>" +
               this._getBaseUnit() +
               "</span>";
            unitPrice.innerHTML = unitPriceContent;
         } else if (unitPriceWrapper) {
            unitPriceWrapper.classList.add("m:hidden");
         }
      }

      _getBaseUnit() {
         const { reference_value, reference_unit } =
            this.currentVariant.unit_price_measurement;
         return reference_value === 1
            ? reference_unit
            : `${reference_value}${reference_unit}`;
      }

      updateButton(disable = true, text) {
         const productForms = document.querySelectorAll(
            `.product-form-${this.sectionId}`
         );
         if (!productForms.length) return;

         productForms.forEach((productForm) => {
            const addButton = productForm.querySelector('[name="add"]');
            if (!addButton) return;

            const dynamicCheckout = productForm.querySelector(
               ".m-product-dynamic-checkout"
            );
            const addButtonText = addButton.querySelector(
               ".m-add-to-cart--text"
            );

            const updateButtonState = (isDisabled) => {
               addButton.disabled = isDisabled;
               addButton.classList.toggle("disabled", isDisabled);
               if (dynamicCheckout) {
                  dynamicCheckout.classList.toggle("disabled", isDisabled);
               }
               addButtonText.textContent = text ? text : window.MinimogStrings.addToCart;
            };

            updateButtonState(disable);
         });
      }

      updateVariantPickerVisibility() {
   if (!this.currentVariant) return;

   const variantMetafield = this.variantMetafieldData.find(
      (item) => String(item.id) === String(this.currentVariant.id)
   );

   const allowedColors = String(
      variantMetafield?.metafields?.allowed_colors ?? ""
   ).trim();

   // 필터 데이터가 없으면 임의로 스와치를 숨기지 않습니다.
   if (!allowedColors) return;

   let colorsText = allowedColors;

   if (allowedColors.includes(":")) {
      const deviceIndex = this.productData.options.findIndex(
         (option) =>
            String(option.name ?? option).trim().toLowerCase() === "device"
      );

      if (deviceIndex < 0) return;

      // 라디오/드롭다운 대신 실제 선택된 variant에서 Device를 읽습니다.
      const currentDevice = String(
         this.currentVariant.options[deviceIndex] ?? ""
      ).trim();

      const matchingEntry = allowedColors.split(";").find((entry) => {
         const separator = entry.indexOf(":");
         return separator >= 0 &&
            entry.slice(0, separator).trim() === currentDevice;
      });

      if (!matchingEntry) return;

      colorsText = matchingEntry.slice(matchingEntry.indexOf(":") + 1);
   }

   const allowed = new Set(
      colorsText.split(",").map((color) => color.trim()).filter(Boolean)
   );

   this.domNodes.optionNodes.forEach((node) => {
      const input = node.querySelector('input[type="radio"]');
      if (!input) return;

      const name = input.name.trim().toLowerCase();
      if (name !== "color" && name !== "colour") return;

      node.style.display = allowed.has(input.value.trim()) ? "" : "none";
   });
}

      updateProductMeta() {
         const { available, sku, barcode } = this.currentVariant;
         const { inStock, outOfStock } = window.MinimogStrings;
         const productAvailability = this.productWrapper.querySelector(
            this.selectors.productAvailability
         );
         const productSku = this.productWrapper.querySelector(
            this.selectors.productSku
         );
         const productBarcode = this.productWrapper.querySelector(
            this.selectors.productBarcode
         );
         const productBanner = this.productWrapper.querySelector(
            this.selectors.productBanner
         );
         const productCustomLabel = this.productWrapper.querySelector(
            this.selectors.productCustomLabel
         );
         const productSubtitle = this.productWrapper.querySelector(
            this.selectors.productSubtitle
         );
         const productInventoryStatus = this.productWrapper.querySelector(
            this.selectors.productInventoryStatus
         );
         const variantMetafield = this.variantMetafieldData.find(
            (metafield) => metafield.id === `${this.currentVariant.id}`
         );
         const invStatus = String(variantMetafield?.metafields?.inventory_status ?? "");

         if (productCustomLabel) {
            if (variantMetafield?.metafields?.custom_label) {
               productCustomLabel.textContent =
                  variantMetafield?.metafields?.custom_label;
               productCustomLabel.style.display = "block";
            } else {
               productCustomLabel.textContent = "";
               productCustomLabel.style.display = "none";
            }
         }
         if (productSubtitle) {
            if (variantMetafield?.metafields?.subtitle) {
               productSubtitle.textContent =
                  variantMetafield?.metafields?.subtitle;
               productSubtitle.style.display = "block";
            } else {
               productSubtitle.textContent = "";
               productSubtitle.style.display = "none";
            }
         }

         if (productSku) {
            productSku.textContent = sku || "N/A";
         }
         if (productBarcode) {
            productBarcode.textContent = barcode || "N/A";
         }

         if (invStatus.toLowerCase().includes("preorder")) {
            this.ctaBtnText = "Preorder"
         } else if (invStatus.toLowerCase().includes("coming soon")) {
            this.ctaBtnText = "Coming soon"
         } else if (invStatus.toLowerCase().includes("backorder")) {
            this.ctaBtnText = "Backorder"
         } else if (!available) {
            this.ctaBtnText = window.MinimogStrings.soldOut
         } else {
            this.ctaBtnText = window.MinimogStrings.addToCart
         }

         if (
            productInventoryStatus &&
            variantMetafield?.metafields?.inventory_status &&
            variantMetafield.metafields.inventory_status !== "SOLD OUT" &&
            variantMetafield.metafields.inventory_status !== "COMING SOON"
         ) {
            productInventoryStatus.textContent =
               variantMetafield.metafields.inventory_status.split("- ")[1];
            productInventoryStatus.style.display = "block";
         } else {
            productInventoryStatus.style.display = "none";
         }

         if (productBanner) {
            // Clear existing banner content
            productBanner.innerHTML = "";
            // Check if we have variant metafield data with a banner
            if (variantMetafield?.metafields?.variant_banner) {
               const bannerValues = variantMetafield.metafields.variant_banner
                  .split(",")
                  .map((v) => v.trim().toLowerCase());
               const bannerConfig = {
                  google: {
                     src: "https://cdn.shopify.com/s/files/1/0808/0067/files/variant_banner_google.png?v=1738869032",
                     width: "140",
                     alt: "Google",
                  },
                  samsung: {
                     src: "https://cdn.shopify.com/s/files/1/0808/0067/files/variant_banner_samsung.png?v=1738869032",
                     width: "140",
                     alt: "Samsung",
                  },
                  qi2: {
                     src: "https://cdn.shopify.com/s/files/1/0808/0067/files/variant_banner_qi2.png?v=1738869033",
                     width: "65",
                     alt: "qi2",
                  },
               };
               bannerValues.forEach((value) => {
                  if (bannerConfig[value]) {
                     const newBanner = document.createElement("img");
                     const config = bannerConfig[value];
                     newBanner.src = config.src;
                     newBanner.width = config.width;
                     newBanner.alt = config.alt;
                     newBanner.style.height = "auto";
                     productBanner.appendChild(newBanner);
                  }
               });
            }
         }

         if (productAvailability) {
            productAvailability.textContent = available ? inStock : outOfStock;
            productAvailability.classList.toggle(
               "m-product-availability--outofstock",
               !available
            );
         }

         // Emit a custom event for product meta update
         const metaUpdateEvent = new CustomEvent("product:meta-updated", {
            detail: { available, sku },
         });
         this.productWrapper.dispatchEvent(metaUpdateEvent);
      }

      setUnavailable() {
         const button = document.getElementById(
            `product-form-${this.sectionId}`
         );
         if (!button) return;

         const addButton = button.querySelector('[name="add"]');
         if (!addButton) return;

         const addButtonText = addButton.querySelector(".m-add-to-cart--text");
         if (addButtonText) {
            addButtonText.textContent = window.MinimogStrings.unavailable;
         }

         const priceWrapper = this.productWrapper.querySelector(".m-price");
         if (priceWrapper) {
            priceWrapper.classList.add("m:visibility-hidden");
         }
      }

      hideSoldOutAndUnavailableOptions() {
         const classes = {
            soldOut: "m-product-option--node__soldout",
            unavailable: "m-product-option--node__unavailable",
         };
         const { optionNodes } = this.domNodes;
         const { variants, options } = this.productData;

         optionNodes.forEach((optNode) => {
            const { optionPosition, value } = optNode.dataset;
            const optPos = Number(optionPosition);
            const isSelectOption = optNode.tagName === "OPTION";

            let matchVariants = [];
            if (optPos === options.length) {
               const optionsArray = [...this.currentVariant.options];
               optionsArray[optPos - 1] = value;
               matchVariants.push(
                  getVariantFromOptionArray(this.productData, optionsArray)
               );
            } else {
               matchVariants = variants.filter(
                  (v) =>
                     v.options[optPos - 1] === value &&
                     v.options[optPos - 2] ===
                        this.currentVariant[`option${optPos - 1}`]
               );
            }

            matchVariants = matchVariants.filter(Boolean);
            const isSoldOut =
               matchVariants.length && matchVariants.every((v) => !v.available);
            const isUnavailable = !matchVariants.length;

            optNode.classList.toggle(classes.soldOut, isSoldOut);
            optNode.classList.toggle(classes.unavailable, isUnavailable);

            if (isSelectOption) {
               optNode.disabled = isUnavailable;
            }
         });
      }

      getVariantData() {
         if (this.variantData) {
            return this.variantData;
         }
         const productVariants = this.querySelector(
            '#productVariants[type="application/json"]'
         );
         if (productVariants) {
            try {
               this.variantData = JSON.parse(productVariants.textContent);
            } catch (error) {
               console.error("Error parsing variant data:", error);
            }
         }
         return this.variantData || {};
      }
      getVariantMetafieldData() {
         if (this.variantMetafieldData) {
            return this.variantMetafieldData;
         }

         // Try both the generic name selector and ID-based selector
         const variantMetafieldElement = this.querySelector(
            'script[type="application/json"][name="sq_variant_metafields"]'
         );
         if (variantMetafieldElement) {
            try {
               this.variantMetafieldData = JSON.parse(
                  variantMetafieldElement.textContent
               );
            } catch (error) {
               console.error("Error parsing variant metafield data:", error);
               this.variantMetafieldData = [];
            }
         } else {
            console.warn("Variant metafield element not found");
            this.variantMetafieldData = [];
         }

         return this.variantMetafieldData;
      }

      getVariantGroupImageData() {
         if (this.variantGroupImages) {
            return this.variantGroupImages;
         }
         const variantGroupElement = this.querySelector(
            '#variantGroup[type="application/json"]'
         );
         if (variantGroupElement) {
            try {
               this.variantGroupImages = JSON.parse(
                  variantGroupElement.textContent
               );
            } catch (error) {
               console.error("Error parsing variant group images data:", error);
            }
         }
         return this.variantGroupImages || {};
      }

      initOptionSwatches() {
         const { _colorSwatches = [], _imageSwatches = [] } =
            window.MinimogSettings;

         if (!this.domNodes.optionNodes) return;

         // Get selected device to filter variants
         let selectedDevice;
         this.domNodes.pickerFields.forEach((field) => {
            const selectedValue = field.dataset.selectedValue;
            if (field.dataset.optionName === "Device") {
               selectedDevice = selectedValue;
            }
         });

         // Filter variants based on selected device
         let availableVariants = [];
         if (selectedDevice) {
            for (let variant of this.productData.variants) {
               if (variant.options.length > 2) {
                  const variantDeviceOption = variant.option1;
                  if (variantDeviceOption === selectedDevice) {
                     availableVariants.push(variant);
                  }
               } else {
                  availableVariants.push(variant);
               }
            }
         } else {
            availableVariants = this.productData.variants;
         }

         this.domNodes.optionNodes.forEach((optNode) => {
            const {
               optionType,
               optionPosition,
               value: optionValue,
            } = optNode.dataset;
            const optionValueLowerCase =
               optionValue && optionValue.toLowerCase();

            // Find appropriate variant for swatch image
            let variantToShowSwatchImage;

            if (
               optNode.closest("[data-option-name]")?.dataset.optionName ===
               "Color"
            ) {
               // For color options, find a variant from filtered variants
               variantToShowSwatchImage = availableVariants.find((variant) => {
                  if (variant.options.length > 2) {
                     return variant.option2 === optionValue;
                  } else {
                     return variant.option1 === optionValue;
                  }
               });
            } else {
               // For other options, use original logic
               variantToShowSwatchImage = this.variantData.find(
                  (v) => v[`option${optionPosition}`] === optionValue
               );
            }

            const variantImage =
               variantToShowSwatchImage &&
               variantToShowSwatchImage.featured_image &&
               variantToShowSwatchImage.featured_image.src
                  ? getSizedImageUrl(
                       variantToShowSwatchImage.featured_image.src,
                       "100x"
                    )
                  : null;
            const customImage = _imageSwatches.find(
               (i) => i.key === optionValueLowerCase
            );
            const customColor = _colorSwatches.find(
               (c) => c.key === optionValueLowerCase
            );

            const labelElement = optNode.querySelector("label");
            if (!labelElement) return;

            if (variantImage || (customImage && customImage.value)) {
               labelElement.classList.add("has-bg-img");
            }

            labelElement.classList.remove("option-loading");

            switch (optionType) {
               case "default":
               case "image":
                  // Decode any HTML entities in the URL before setting it
                  const imageUrl =
                     variantImage || (customImage && customImage.value) || "";
                  if (imageUrl) {
                     const decodedUrl = imageUrl
                        .replaceAll(/&quot;/g, "'")
                        .replaceAll(/&amp;/g, "&");
                     // Extract the base URL without any existing parameters
                     const baseUrl = decodedUrl.split("?")[0];
                     // Apply Shopify's image transformation
                     const transformedUrl = `${baseUrl}?width=110&height=150&crop=center`;
                     labelElement.style.backgroundImage = `url(${transformedUrl})`;
                     labelElement.style.backgroundSize = "cover";
                     labelElement.style.backgroundPosition = "center center";
                  }
                  break;
               case "color":
                  labelElement.style.backgroundColor =
                     (customColor && customColor.value) || optionValueLowerCase;
                  if (customImage && customImage.value) {
                     const decodedCustomUrl = String(customImage.value)
                        .replaceAll(/&quot;/g, "'")
                        .replaceAll(/&amp;/g, "&");
                     labelElement.style.backgroundImage = `url(${decodedCustomUrl})`;
                  }
                  break;
               default:
                  break;
            }
         });
      }
   }

   customElements.define("variant-picker", VariantPicker);
}
if (!customElements.get("variant-button")) {
   class VariantButton extends HTMLElement {
      constructor() {
         super();
         this.selectedSpan = this.querySelector(".option-label--selected");
         this.addEventListener("change", this.updateSelectedValue);

         // Get the option position to identify related components
         this.optionName = this.dataset.optionName;

         // Find related variant-select with same option position
         this.relatedSelect = this.closest("variant-picker").querySelector(
            `variant-select[data-option-name="${this.optionName}"]`
         );
      }

      updateSelectedValue() {
         const checkedInput = Array.from(this.querySelectorAll("input")).find(
            (radio) => radio.checked
         );
         if (!checkedInput) return;

         this.value = checkedInput.value;
         this.setAttribute("data-selected-value", this.value);

         if (this.selectedSpan) {
            this.selectedSpan.textContent = this.value;
            this.selectedSpan.classList.remove("m:text-color-error");
         }

         // Update related select if it exists
         if (this.relatedSelect) {
            this.relatedSelect.dataset.selectedValue = this.value;
            const select = this.relatedSelect.querySelector("select");
            const options = select.options;
            for (let i = 0; i < options.length; i++) {
               if (options[i].value === this.value) {
                  options[i].selected = true;
                  break;
               } else {
                  options[i].selected = false;
               }
            }
            this.relatedSelect.updateSelectedValue();
         }
      }
   }

   customElements.define("variant-button", VariantButton);

   if (!customElements.get("variant-select")) {
      class VariantSelect extends VariantButton {
         constructor() {
            super();
            this.optionName = this.dataset.optionName;
            // Find related variant-button with same option position
            this.relatedButton = this.closest("variant-picker").querySelector(
               `variant-button[data-option-name="${this.optionName}"]`
            );
         }

         updateSelectedValue() {
            const select = this.querySelector("select");
            if (!select) return;

            this.value = select.value;
            this.setAttribute("data-selected-value", this.value);

            if (this.selectedSpan) {
               this.selectedSpan.textContent = this.value;
               this.selectedSpan.classList.remove("m:text-color-error");
            }

            // Update related radio button if it exists
            if (this.relatedButton) {
               this.relatedButton.dataset.selectedValue = this.value;
               const nodes = this.relatedButton.querySelectorAll(
                  ".m-product-option--node"
               );
               for (const node of nodes) {
                  const input = node.querySelector("input");
                  if (input && input.value === this.value) {
                     input.checked = true;
                     break;
                  } else {
                     input.checked = false;
                  }
               }
            }
         }
      }

      customElements.define("variant-select", VariantSelect);
   }
   if (!customElements.get("variant-image")) {
      class VariantImage extends VariantButton {
         constructor() {
            super();
         }
      }

      customElements.define("variant-image", VariantImage);
   }
   if (!customElements.get("variant-color")) {
      class VariantColor extends VariantButton {
         constructor() {
            super();
         }
      }
      customElements.define("variant-color", VariantColor);
   }
}
